import { useNavigate } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { walkthroughStatusLabel } from "@/components/experience/vocabulary";

function formatDate(value: string | null | Date | undefined): string | null {
  if (!value) return null;
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

interface WorkspaceEyebrowProps {
  runNumber: number | undefined;
  scenarioRuns: any[] | undefined;
  runsChronological: any[];
  walkthroughId: string;
  projectId: string;
  status: string;
  completedAt?: string | null;
}

export function WorkspaceEyebrow({
  runNumber,
  scenarioRuns,
  runsChronological,
  walkthroughId,
  projectId,
  status,
  completedAt,
}: WorkspaceEyebrowProps) {
  const navigate = useNavigate();
  const isComplete = status === "completed";
  const isFailed = status === "failed";
  const isRunning = status === "running" || status === "pending";
  const isCancelled = status === "cancelled";

  return (
    <div className="flex items-center gap-2 text-xs">
      {runNumber &&
        (scenarioRuns && scenarioRuns.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="h-6 px-1.5 py-0.5 text-xs font-semibold gap-1 text-foreground hover:bg-muted/80 rounded"
                aria-label={`Switch run (currently Run #${runNumber})`}
              >
                <span>Run #{runNumber}</span>
                <ChevronDown className="size-3 text-muted-foreground shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-64 max-h-80 overflow-y-auto"
            >
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                Runs for this scenario ({scenarioRuns.length})
              </div>
              <DropdownMenuSeparator />
              {runsChronological
                .slice()
                .reverse()
                .map((r) => {
                  const num =
                    r.runNumber ??
                    runsChronological.findIndex((x) => x.id === r.id) + 1;
                  const isCurrent = r.id === walkthroughId;
                  const pCount = r.problemCounts?.found ?? r.findingCount ?? 0;
                  return (
                    <DropdownMenuItem
                      key={r.id}
                      onClick={() => {
                        if (!isCurrent) {
                          navigate(
                            `/projects/${projectId}/walkthroughs/${r.id}`,
                          );
                        }
                      }}
                      className={cn(
                        "flex items-center justify-between text-xs cursor-pointer py-2",
                        isCurrent && "bg-muted font-medium",
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className={cn(
                            "size-2 rounded-full shrink-0",
                            r.status === "completed" && "bg-finding-helps",
                            r.status === "failed" && "bg-destructive",
                            (r.status === "running" ||
                              r.status === "pending") &&
                              "bg-severity-important animate-pulse",
                          )}
                        />
                        <span className="font-semibold text-foreground shrink-0">
                          Run #{num}
                        </span>
                        <span className="text-muted-foreground truncate">
                          · {pCount} {pCount === 1 ? "problem" : "problems"}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0 pl-2">
                        {formatDate(r.startedAt ?? r.createdAt) && (
                          <span className="text-2xs text-muted-foreground">
                            {formatDate(r.startedAt ?? r.createdAt)}
                          </span>
                        )}
                        {isCurrent && (
                          <Check className="size-3.5 text-primary" />
                        )}
                      </div>
                    </DropdownMenuItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="font-semibold text-foreground">
            Run #{runNumber}
          </span>
        ))}
      <span className="text-muted-foreground">·</span>
      <span
        className={cn(
          "font-medium",
          isComplete && "text-finding-helps",
          isFailed && "text-destructive",
          isRunning && "text-severity-important",
          isCancelled && "text-muted-foreground",
        )}
      >
        {walkthroughStatusLabel(status)}
      </span>
      {completedAt && (
        <>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">
            {formatDate(completedAt)}
          </span>
        </>
      )}
    </div>
  );
}
